import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { Button } from '@/components/ui/Button'

/**
 * Фильтр канбана заказов по городу. Мультивыбор: пустой список = показывать
 * все. Список доступных городов передаётся снаружи (берётся из загруженных
 * заказов). Выбор запоминается вызывающей стороной (uiStore → localStorage).
 */
export function KanbanCityFilter({
  cities,
  selected,
  onChange,
}: {
  cities: string[]
  selected: string[]
  onChange: (v: string[]) => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const active = selected.length > 0

  useEffect(() => {
    if (!open) return
    function onPointer(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  function toggle(city: string) {
    const set = new Set(selected)
    if (set.has(city)) set.delete(city)
    else set.add(city)
    onChange([...set])
  }

  return (
    <div ref={ref} className="relative">
      <Button
        type="button"
        variant="outline"
        onClick={() => setOpen((o) => !o)}
        className="!normal-case !tracking-normal"
      >
        {t('geo.cityFilter.city')}
        {active ? (
          <span className="rounded-[var(--stryzh-radius-pill)] bg-[color:var(--stryzh-color-accent-orange)] px-2 py-0.5 font-[family-name:var(--stryzh-font-family-mono)] text-[length:var(--stryzh-font-size-xs)] font-bold text-[color:var(--stryzh-color-text-on-orange-badge)]">
            {selected.length}
          </span>
        ) : null}
      </Button>
      {open ? (
        <div className="absolute right-0 z-50 mt-2 w-[min(300px,80vw)] rounded-xl border border-[var(--stryzh-color-border-default)] bg-[var(--stryzh-color-surface)] p-3 shadow-[var(--stryzh-shadow-dropdown,0_8px_24px_rgba(0,0,0,0.18))]">
          {cities.length === 0 ? (
            <p className="py-2 text-center text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
              {t('geo.cityFilter.noCities')}
            </p>
          ) : (
            <>
              <div className="flex max-h-[280px] flex-wrap gap-2 overflow-y-auto">
                {cities.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={clsx(
                      'stryzh-filter-chip',
                      selected.includes(c) && 'stryzh-filter-chip--selected',
                    )}
                    onClick={() => toggle(c)}
                  >
                    {c}
                  </button>
                ))}
              </div>
              {active ? (
                <div className="mt-3 flex justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => onChange([])}
                  >
                    {t('geo.cityFilter.reset')}
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </div>
  )
}

/**
 * Явный индикатор того, что фильтр по городу активен. Акцентная полоса со
 * списком выбранных городов; каждый чип убирается по клику, есть «Сбросить».
 */
export function KanbanCityFilterBanner({
  selected,
  onChange,
}: {
  selected: string[]
  onChange: (v: string[]) => void
}) {
  const { t } = useTranslation()
  if (selected.length === 0) return null
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-[color:var(--stryzh-color-status-badge-accent-border)] bg-[color:var(--stryzh-color-status-badge-accent-bg)] px-3 py-2">
      <span className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-xs)] font-semibold text-[color:var(--stryzh-color-accent-orange)]">
        {t('geo.cityFilter.bannerLabel')}
      </span>
      {selected.map((c) => (
        <button
          key={c}
          type="button"
          className="inline-flex items-center gap-1 rounded-[var(--stryzh-radius-pill)] border border-[color:var(--stryzh-color-status-badge-accent-border)] bg-[color:var(--stryzh-color-surface)] px-2 py-0.5 text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-accent-orange)]"
          onClick={() => onChange(selected.filter((x) => x !== c))}
          aria-label={t('geo.cityFilter.removeCity', { city: c })}
        >
          {c}
          <span aria-hidden>×</span>
        </button>
      ))}
      <button
        type="button"
        className="ml-auto text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)] underline"
        onClick={() => onChange([])}
      >
        {t('geo.cityFilter.reset')}
      </button>
    </div>
  )
}
